import { skipToken } from "@reduxjs/toolkit/dist/query";
import {
  BidStatus,
  useListBidsForEventQuery,
  useListNegotiationRoundsQuery,
  type NegotiationRoundDto,
  type SourcingEventDto,
} from "../../../autogen/bff-api";
import { allowsPartIIDialogue } from "./partIIDialogue";
import {
  roundInviteCandidates,
  type RoundInviteCandidate,
} from "./roundInviteCandidates";

export interface DialogueRoster {
  candidates: RoundInviteCandidate[];
  rounds: NegotiationRoundDto[];
  rejectedOrganizationIds: string[];
  reducedOrganizationIds: string[];
  reducibleOrganizationIds: string[];
  didNotAnswerPreviousRound: { roundNumber: number; organizationIds: string[] } | null;
  leavesOutTenderers: boolean;
  tenderersLoading: boolean;
  tenderersFailed: boolean;
  tenderersRetrying: boolean;
  refetchTenderers: () => void;
}

export const useDialogueRoster = (
  event: SourcingEventDto,
  round: NegotiationRoundDto,
): DialogueRoster => {
  const isPartIIDialogue = allowsPartIIDialogue(event);
  const { data: roundList } = useListNegotiationRoundsQuery({
    eventId: event.id,
  });
  const bidsQuery = useListBidsForEventQuery(
    isPartIIDialogue ? { eventId: event.id } : skipToken,
  );
  const tenderers = [
    ...new Set(
      (bidsQuery.data?.bids ?? [])
        .filter(
          (bid) =>
            bid.status !== BidStatus.Draft && bid.forNegotiationRound == null,
        )
        .map((bid) => bid.owner),
    ),
  ];
  const candidates = roundInviteCandidates(event, round, tenderers);
  const rejectedOrganizationIds = roundList?.rejectedOrganizationIds ?? [];
  const previousRound = (roundList?.rounds ?? [])
    .filter((r) => r.roundNumber < round.roundNumber)
    .sort((a, b) => b.roundNumber - a.roundNumber)[0];
  const didNotAnswerPreviousRound = previousRound
    ? {
        roundNumber: previousRound.roundNumber,
        organizationIds: roundList?.didNotAnswerOrganizationIdsByRound?.[previousRound.id] ?? [],
      }
    : null;
  const invited = new Set(round.invites.map((invite) => invite.organizationId));
  const leavesOutTenderers =
    isPartIIDialogue &&
    candidates.some(
      (candidate) =>
        !rejectedOrganizationIds.includes(candidate.organizationId) &&
        !invited.has(candidate.organizationId),
    );
  return {
    candidates,
    rounds: roundList?.rounds ?? [],
    rejectedOrganizationIds,
    reducedOrganizationIds: roundList?.reducedOrganizationIds ?? [],
    reducibleOrganizationIds: roundList?.reducibleOrganizationIds ?? [],
    didNotAnswerPreviousRound,
    leavesOutTenderers,
    tenderersLoading: isPartIIDialogue && bidsQuery.isLoading === true,
    tenderersFailed: isPartIIDialogue && bidsQuery.isError === true,
    tenderersRetrying: isPartIIDialogue && bidsQuery.isFetching === true,
    refetchTenderers: () => void bidsQuery.refetch?.(),
  };
};

export const selectionJustificationMissing = (
  roster: DialogueRoster,
  justification: string | null | undefined,
) => roster.leavesOutTenderers && (justification ?? "").trim() === "";
